import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { api } from './api.js'
import * as Auth from './auth.js'

/** Fetches feature-flag-backed funding option visibility for the active wallet. */
export function useFundingOptions(): UseQueryResult<useFundingOptions.Data> {
  const address = Auth.useSessionAddress()
  return useQuery({
    enabled: Boolean(address),
    placeholderData: {
      applePay: false,
      coinflow: false,
      coinflowAdditionalPaymentTypes: false,
      moonpay: false,
      stripeCheckout: false,
      stripeEuCheckout: false,
    },
    queryFn: async () => {
      const res = await api.api['feature-flags']['funding-options'].$get()
      if (!res.ok) throw new Error('Failed to fetch funding options.')
      return res.json()
    },
    queryKey: Auth.session.queryKey(address, 'feature-flags', 'funding-options'),
    staleTime: 60_000,
  })
}

export declare namespace useFundingOptions {
  /** Funding option visibility returned by the worker. */
  type Data = {
    /** Whether Apple Pay should appear as a funding option. */
    applePay: boolean
    /** Whether Coinflow credits should show additional wallet payment types. */
    coinflowAdditionalPaymentTypes: boolean
    /** Whether Coinflow credits should appear as a funding option. */
    coinflow: boolean
    /** Whether MoonPay headless onramp should appear as a funding option. */
    moonpay: boolean
    /** Whether Stripe headless checkout should appear as a funding option. */
    stripeCheckout: boolean
    /** Whether Stripe headless checkout should appear for EU profile countries. */
    stripeEuCheckout: boolean
  }
}
